
import { Root } from '@/main';
import { Component, Prop, Vue, toNative, Watch } from 'vue-facing-decorator';
import { Editor as KendoEditor } from '@progress/kendo-vue-editor';
import { EditorView, EditorState } from '@progress/kendo-editor-common';
import { EditorUtils } from '@progress/kendo-vue-editor';
import { TelerikImageUploadViewModel } from '@/entities/TelerikImageUploadViewModel';
import Common from '@/helper/Common';
import ModuleTypeEnum from '@/enums/ModuleTypeEnum';

@Component({
  components: {
    KendoEditor,
  },
})
class Editor extends Vue {
  @Prop() private description?: string;
  @Prop() private isReadOnly?: boolean;
  @Prop() private imagesToBeUploaded?: TelerikImageUploadViewModel[];
  @Prop() private moduleType?: string;
  private localText?: string = '';
  private localIsReadOnly = false;
  private isResizable = false;
  private addIcon = require('@/assets/Images/add-icon-grey.svg');
  private uploadedImage = '';
  private fileToUpload: File | null = null;
  private uploadedImagesData: TelerikImageUploadViewModel[] = [];
  private isInsertHyperLinkEmpty = false;
  private hyperLinkDisplayText = '';
  private htmlAreaRefValue = '';
  private textAreaRefValue = '';
  private maxImageHeight = 150;
  private maxImageWidth = 150;
  private tools: string[][] = [
    ['Bold', 'Italic', 'Underline'],
    ['Link', 'Unlink'],
  ];
  private imageHyperlinkUrl  = '';
  private availableTags: string[] = [];
  private blockLevelHTMLTags: string[] = ['div','h1','h2','h3','h4','h5','h6','dt','dd','figcaption','hr','pre','li','address','blockquote','tr'];
  // Listeners
  private onImageFileChange?: EventListener;

  // Getters
  private GetEditorInlineStyle() {
    return {
      resize: 'vertical',
      minHeight: '200px',
    };
  }

  private getInputTextValue() {
    this.localText = this.description || '';
  }

  private mounted() {
    this.getInputTextValue();
    this.localIsReadOnly = this.isReadOnly !== null && this.isReadOnly !== undefined ? this.isReadOnly : this.localIsReadOnly;
    this.isResizable = true;
    if(this.moduleType && this.moduleType === ModuleTypeEnum.Invite) {
      this.tools.push(['OrderedList', 'UnorderedList']);
      this.tools.push(['AlignLeft', 'AlignCenter', 'AlignRight']);
      this.tools.push(['Undo', 'Redo']);
      this.tools.push(['InsertImage']);
    }
    this.availableTags = this.BuildAllowedTags();
    this.$nextTick(() => {
      // Get the editor root element to target insert image button
      const root = this.GetEditorRootElement();
      if (!root) {
        // Could not find editor root element
        return;
      }
      if(this.moduleType && this.moduleType === ModuleTypeEnum.Invite) {
      // Get the insert image button
        const nativeButton = this.FindUploadImageButton(root);
        if(!!nativeButton) {
          nativeButton.addEventListener('click', this.HandleImageUpload);
        }
      }

      const linkBtn = this.FindLinkButton(root);
      if(!!linkBtn) {
        linkBtn.addEventListener('click', this.HandleLinkInsertion);
      }
    });
    if(!!this.imagesToBeUploaded) {
      this.uploadedImagesData = this.imagesToBeUploaded;
    }
    // clear description box after submit the form.
    Root.on('clearDescription', (description: string) => {
      this.localText = description || '';
    });
    // update description
    Root.on('UpdateEditorDescription', (description: string) => {
      this.localText = description || '';
    });
  }
  private OnEditorLoaded(event: any) {
    const iframeDoc = event.target?.iframe?.contentDocument;
    if(!!iframeDoc) {
      const style = iframeDoc.createElement('style');
      style.textContent = `
            .k-content.ProseMirror {
              font-size: 13px;
              color: #333;
              font-family: "Open Sans", Helvetica, Arial, sans-serif;
            }
            .k-content.ProseMirror img {
              vertical-align: text-bottom;
            }
            .k-content.ProseMirror a {
              color: #1E3A8A;
            }`;
      iframeDoc.head.appendChild(style);
    }
  }
  private OnExecute(event: any) {
    const { doc, selection } = event.transaction;
    let selectionHtml = '';
    let selectionText = '';
    if (!selection.empty) {
      const schema = event.view?.state?.schema || doc.type?.schema;
      const linkMark = schema?.marks?.link || schema?.marks?.hyperlink;

      let from: number = selection.from;
      let to: number = selection.to;

      // If any part of the selection intersects a link, expand to the full link text
      if (linkMark) {
        const linkRange = this.ExtractAnchorTagStartAndEndPoint(doc, from, to, linkMark);
        if (linkRange) {
          // Check if user selection is fully inside the link mark boundaries
          if (from >= linkRange.from && to <= linkRange.to) {
            from = linkRange.from;
            to = linkRange.to;
          }
        }
      }
      const node = doc.cut(from, to);

      selectionHtml = EditorUtils.getHtml({
        doc: node,
        schema: node.type.schema,
      });
      selectionText = node.textContent;
    }
    this.htmlAreaRefValue = selectionHtml;
    this.textAreaRefValue = selectionText;
  }
  // Use to get the full text inside the anchor tag even if the user have selected small part of it
  private ExtractAnchorTagStartAndEndPoint(doc: any, from: number, to: number, markType: any) {
    // Fallback: walk left/right inside the parent text to include the full mark span
    const $from = doc.resolve(from);
    const parent = $from.parent;
    const start = $from.start();
    const end = $from.end();
    // If not inside text, bail
    if (!parent || !parent.isTextblock) {
      return null;
    }
    // Find a position inside the mark (prefer start of selection)
    let pos = from;
    let $pos = doc.resolve(pos);
    const hasLinkAt = (rp: any) => rp.marks().some((m: any) => m.type === markType);
    if (!hasLinkAt($pos)) {
    // Try the other end of the selection
      pos = to;
      $pos = doc.resolve(pos);
      if (!hasLinkAt($pos)) {
        return null;
      }
    }
    // Expand left
    let left = pos;
    while (left > start) {
      const $l = doc.resolve(left);
      if (!hasLinkAt($l)) {
        break;
      }
      left--;
    }
    // Expand right
    let right = pos;
    while (right < end) {
      const $r = doc.resolve(right);
      if (!hasLinkAt($r)) {
        break;
      }
      right++;
    }

    return left < right ? { from: left, to: right } : null;
  }

  // Method to extend the editor view with custom plugins
  private ExtendView(event: { dom: HTMLElement; viewProps: { state: any } }) {
    const { imageResizing } = EditorUtils;
    const state = event.viewProps.state;
    const plugins = [imageResizing(),
      ...state.plugins,
    ];
    return new EditorView(
      {
        mount: event.dom,
      },
      {
        ...event.viewProps,
        state: EditorState.create({
          doc: state.doc,
          plugins,
        }),
      },
    );
  }

  // Emits the updated HTML content to parent when the editor changes.
  private OnChange(event: { html: string }) {
    let updatedHtml = event.html;

    // Linkify phone numbers found in plain text
    const linkedHtml = this.LinkPhoneNumbers(updatedHtml);
    if (linkedHtml !== updatedHtml) {
      this.localText = linkedHtml;
      updatedHtml = linkedHtml;
    }

    const doc = new DOMParser().parseFromString(updatedHtml, 'text/html');
    const imgs = Array.from(doc.querySelectorAll('img[src]:not(.prosemirror-separator):not([id])'));
    if(imgs.length > 0) {
      const image = new Image();
      image.src = this.uploadedImage;
      image.onload = () => {
        for (const img of imgs) {
          if (!img.hasAttribute('id')) {
            const id = Common.GenerateGuid();
            img.setAttribute('id', id);
            const imageTobeUploaded = TelerikImageUploadViewModel.createEmpty();
            imageTobeUploaded.Id = id;
            imageTobeUploaded.File = this.fileToUpload;
            imageTobeUploaded.ImageWidth = image.width;
            imageTobeUploaded.ImageHeight = image.height;
            this.uploadedImagesData.push(imageTobeUploaded);
          }
        }
        // NOW, after all <img> are updated, reserialize and emit:
        updatedHtml = doc.body.innerHTML;
        this.$emit('updatedDescription', updatedHtml);
        this.$emit('images-to-be-uploaded', this.uploadedImagesData);
      };
    }
    this.$emit('updatedDescription', updatedHtml);
  }

  // Gets invoked when insert image button is clicked
  private HandleImageUpload() {
    this.UpdateImageHyperlinkFromSelection();
    const imageWidthInputElement = document.getElementById('k-editor-image-width') as HTMLInputElement;
    const imageHeightInputElement = document.getElementById('k-editor-image-height') as HTMLInputElement;
    const imageWidthInputParentDiv = imageWidthInputElement.closest('.k-form-field') as HTMLElement;
    const imageHeightInputParentDiv = imageHeightInputElement.closest('.k-form-field') as HTMLElement;
    if (imageWidthInputParentDiv) {
      imageWidthInputParentDiv.style.display = 'none'; // hides the div
    }
    if (imageHeightInputParentDiv) {
      imageHeightInputParentDiv.style.display = 'none'; // hides the div
    }
    // Hide the div when the input with id 'k-editor-image-url' is found
    const input = document.getElementById('k-editor-image-url');
    if (!!input) {
      const parentDiv = input.closest('.k-form-field') as HTMLElement;
      if (parentDiv) {
        parentDiv.style.display = 'none'; // hides the div
        this.InjectImageDialogFieldAsHtml();
      }
    }
  }

  // Gets invoked when insert image button is clicked
  private HandleLinkInsertion() {
    this.ValidateEditorLink();

    // NEW: check if current selection is an image
    const isImageSelected = this.UpdateImageHyperlinkFromSelection();
    // Hide the div when the input with id 'k-editor-image-url' is found
    const chkBoxElement = document.getElementById('k-editor-link-target') as HTMLInputElement;
    const titleInputElement = document.getElementById('k-editor-link-text') as HTMLInputElement;
    const titleInputParentDiv = titleInputElement.closest('.k-form-field') as HTMLElement;
    const chkBoxParentDiv = chkBoxElement.closest('.k-form-field') as HTMLElement;
    if (titleInputParentDiv) {
      titleInputParentDiv.style.display = 'none'; // hides the div
    }
    if (chkBoxParentDiv) {
      chkBoxParentDiv.style.display = 'none'; // hides the div
    }
    if (!isImageSelected) {
      const html = `<div class="k-form-field">
                    <label for="txtDisplayText" class="k-form-label">${this.$t('DisplayText').toString()}</label>
                    <div class="k-form-field-wrap">
                      <input id="txtDisplayText"
                             type="text"
                             class="k-input"
                             value="${this.textAreaRefValue}" />
                    </div>
                  </div>`;

      this.InjectInputFieldAsHtml(html, 'k-editor-link-url');

      const webAddressTextElement = document.getElementById('txtDisplayText') as HTMLInputElement | null;
      if (!!webAddressTextElement) {
        this.hyperLinkDisplayText = webAddressTextElement.value;
        webAddressTextElement.addEventListener('input', this.SetHyperLinkDisplayText);
      }
    }

    const webAddressURLInputElement = document.getElementById('k-editor-link-url') as HTMLInputElement | null;
    if (!!webAddressURLInputElement) {
      webAddressURLInputElement.placeholder = 'https://example.com';
      this.isInsertHyperLinkEmpty = !webAddressURLInputElement.value.trim();
      this.imageHyperlinkUrl = webAddressURLInputElement.value.trim();
      webAddressURLInputElement.addEventListener('input', this.ValidateEditorLink);
    }
  }
  private SetHyperLinkDisplayText() {
    const input = document.getElementById('txtDisplayText') as HTMLInputElement | null;
    if(!!input) {
      this.hyperLinkDisplayText = input.value;
    }
  }
  private ValidateEditorLink() {
    const actionBtn = document.querySelector('.k-button-solid-primary');
    const webAddressInputElement = document.getElementById('k-editor-link-url') as HTMLInputElement | null;
    this.isInsertHyperLinkEmpty = !webAddressInputElement || !webAddressInputElement.value.trim();
    if (!!webAddressInputElement) {
      this.imageHyperlinkUrl = webAddressInputElement.value.trim();
    }
    if(!!actionBtn) {
      actionBtn.addEventListener('click', this.ReplaceLinkLabel);
      if(this.isInsertHyperLinkEmpty) {
        actionBtn.classList.add('disabledpage');
      } else {
        actionBtn.classList.remove('disabledpage');
      }
    }
  }
  private ReplaceLinkLabel() {
    const editorRef = this.$refs.editor as any;
    if (!editorRef || !editorRef.view) {
      return;
    }

    const { state, dispatch } = editorRef.view;
    const { doc, selection, schema } = state;
    const linkMarkType = schema.marks.link;
    if (!linkMarkType) {
      return;
    }

    let from: number = selection.from;
    let to: number = selection.to;
    let linkMarkAttrs: any = null;

    // Expand to full link and capture mark attributes
    doc.nodesBetween(from, to, (node: { marks: any[]; nodeSize: any }, pos: number) => {
      const linkMark = node.marks.find((mark) => mark.type === linkMarkType);
      if (linkMark) {
        from = Math.min(from, pos);
        to = Math.max(to, pos + node.nodeSize as number);
        linkMarkAttrs = linkMark.attrs; // keep original mark attributes like href
      }
    });

    if (!linkMarkAttrs) {
      return;
    }
    linkMarkAttrs = {
      ...linkMarkAttrs,
      target: '_blank',
      rel: 'noreferrer',
    };
    const displayText = this.hyperLinkDisplayText || ''; // the new link text to set
    const tr = state.tr;
    if (displayText) {
    // User entered text: replace text + keep link (with new attrs)
      const newTextNode = schema.text(displayText, [linkMarkType.create(linkMarkAttrs)]);
      tr.replaceRangeWith(from, to, newTextNode);
    } else {
    // No display text: keep existing text, just update the link attrs
    // Remove old link mark and add new one with target="_blank"
      tr.removeMark(from, to, linkMarkType);
      tr.addMark(from, to, linkMarkType.create(linkMarkAttrs));
    }
    if (tr.docChanged) {
      dispatch(tr.scrollIntoView());
    }
  }

  // Gets the editor's root DOM element
  private GetEditorRootElement(): HTMLElement | null {
    try {
      // Try to get the editor's DOM element from $refs first
      const editorRef = (this.$refs as any)?.kEditor;
      if (editorRef?.$el) {
        return editorRef.$el as HTMLElement;
      }

      // Fallback to component's root element
      return this.$el as HTMLElement;
    } catch (error) {
      // Error getting editor root element
      return null;
    }
  }

  // Small helper to get common editor selection context for image operations
  private GetImageSelectionContext(){
    // Get reference to the editor component from Vue's $refs
    const editorRef = this.$refs.editor as any;
    if (!editorRef?.view) {
      return null;
    }

    // Destructure state , current selection, schema, imageNode type definition
    const { state } = editorRef.view;
    const { doc, selection, schema } = state;
    const imageNodeType = schema.nodes.image;

    if (!imageNodeType) {
      return null;
    }
    return { doc, selection, schema, imageNodeType };
  }

  // Checks if current selection contains an image and,
  // if so, pre-fills this.imageHyperlinkUrl from its link (if any).
  // Returns true if an image is selected, else false.
  private UpdateImageHyperlinkFromSelection(): boolean {
    const context = this.GetImageSelectionContext();
    if (!context) {
      this.imageHyperlinkUrl = '';
      return false;
    }

    const { doc, selection, schema, imageNodeType } = context;
    const linkMarkType = schema.marks.link || schema.marks.hyperlink;

    if (!imageNodeType || !linkMarkType) {
      this.imageHyperlinkUrl = '';
      return false;
    }

    const { from, to } = selection;
    let hasImage = false;
    let foundHref: string | null = null;

    doc.nodesBetween(from, to, (node: any) => {
      if (node.type === imageNodeType) {
        hasImage = true;

        // capture first href we find
        if (!foundHref) {
          const linkMark = node.marks.find((m: any) => m.type === linkMarkType);
          if (linkMark?.attrs?.href) {
            foundHref = linkMark.attrs.href;
          }
        }
      }
    });

    this.imageHyperlinkUrl = foundHref || '';
    return hasImage;
  }

  // Used to insert type custom upload image control to the editor
  private InjectImageDialogFieldAsHtml() {
    let previouslyUploadedImage = null; // varibale to store previously uploaded image if any
    const webAddress = document.getElementById('k-editor-image-url') as HTMLInputElement;
    if (!!webAddress) {
      // set value of previously uploaded image if any
      previouslyUploadedImage = webAddress.value || null;
    }
    const initialUrl = this.imageHyperlinkUrl || '';
    const html = `
            <div>
              <label for="image-upload" class="k-form-label">${this.$t('UploadImage').toString()}</label>
              <div class="k-form-field-wrap">
                <label for="image-upload" class="upload-image-label" title="${this.$t('UploadImage').toString()}">
                  <img id="imgTelerikImageUpload" class="uploaded-image" src=`+ (previouslyUploadedImage || this.addIcon) + `>
                </label>
                <input id="image-upload" type="file" accept="image/*" class="display-none" />
              </div>
            </div>
            <div class="k-form-field">
              <label for="image-hyperlink-url" class="k-form-label">${this.$t('ImageLinkURL').toString()}</label>
              <div class="k-form-field-wrap">
                <input
                  id="image-hyperlink-url"
                  type="text"
                  class="k-input"
                  value="${initialUrl}"
                  placeholder="https://example.com"
                />
              </div>
            </div>
          `;
    // try immediately, else wait for dialog DOM
    if (this.IsCustomImageUploadedInsertedToThePopup(html)) {
      return;
    }
    const obs = new MutationObserver(() => {
      if (this.IsCustomImageUploadedInsertedToThePopup(html)) {
        obs.disconnect();
      }
    });
    obs.observe(document.body, { childList: true, subtree: true });
  }
  // Insert custom image upload to the editor insert image popup
  private IsCustomImageUploadedInsertedToThePopup(html: string){
    const form = document.querySelector('.k-window-content form.k-form') ;
    if (!form) {
      return false;
    }

    // anchor: after “Alternate text” row, fall back to after URL row, else append
    const anchor =
      form.querySelector('label[for="k-editor-image-alt"]')?.closest('.k-form-field') ||
      form.querySelector('label[for="k-editor-image-url"]')?.closest('.k-form-field');

    if (anchor) {
      (anchor as HTMLElement).insertAdjacentHTML('afterend', html);
    } else {
      form.insertAdjacentHTML('beforeend', html);
    }
    // after inserting HTML
    const input = document.getElementById('image-upload');
    if (input) {
      if (this.onImageFileChange) {
        input.removeEventListener('change', this.onImageFileChange);
      }
      this.onImageFileChange = (e: Event) => this.OnFilePicked(e);
      input.addEventListener('change', this.onImageFileChange);
    }
    // 2) Image link URL input listener
    const linkInput = document.getElementById('image-hyperlink-url') as HTMLInputElement | null;
    if (!!linkInput) {
      linkInput.addEventListener('input', this.SetImageHyperlinkUrl);
    }

    // 3) Hook into the dialog primary action (OK) button
    const actionBtn = document.querySelector('.k-editor-window .k-actions .k-button-solid-primary');

    if (actionBtn) {
    // avoid multiple handlers on reopen
      actionBtn.removeEventListener('click', this.ApplyImageHyperlink );
      actionBtn.addEventListener('click', this.ApplyImageHyperlink );
    }
    return true;

  }

  // Helper to store the URL and apply hyperLink
  private SetImageHyperlinkUrl() {
    const input = document.getElementById('image-hyperlink-url') as HTMLInputElement | null;
    if (input) {
      this.imageHyperlinkUrl = input.value.trim();
    }
  }

  // Called when user clicks the OK button on the Insert Image dialog
  private ApplyImageHyperlink() {
    const editorRef = this.$refs.editor as any;
    if (!editorRef || !editorRef.view) {
      return;
    }

    // Delay the execution so the dialog values (URL input) have time to update
    setTimeout(() => {
      const view = editorRef.view;
      const { state, dispatch } = view;
      const { doc, schema } = state;

      // Identify the link mark type ('link' or 'hyperlink') and the image node type
      const linkMarkType = schema.marks.link || schema.marks.hyperlink;
      const imageNodeType = schema.nodes.image;

      // If either link or image support is missing, stop
      if (!linkMarkType || !imageNodeType) {
        return;
      }

      let from = -1;
      let to = -1;

      // First, try to find the selected image node
      const selection = state.selection;
      const { $from, $to } = selection;

      // Look for selected image node inside the selection range
      doc.nodesBetween($from.pos, $to.pos, (node: any, pos: number) => {
        if (node.type === imageNodeType) {
          from = pos;
          to = pos + node.nodeSize; // Set the range for the selected image
          return false; // Stop iterating once we've found the selected image
        }
      });

      // If no image is selected, fallback to finding the first image in the document
      if (from === -1 || to === -1) {
      // Search for the first image node in the document
        doc.descendants((node: any, pos: number) => {
          if (node.type === imageNodeType) {
            from = pos;
            to = pos + node.nodeSize;
            return false; // Stop after finding the first image
          }
        });
      }

      // If no image was found (which should never happen if the editor has images), exit
      if (from === -1 || to === -1) {
        return;
      }

      const href = (this.imageHyperlinkUrl || '').trim();
      let transaction = state.tr;
      // Remove the existing link mark (if any)
      transaction = transaction.removeMark(from, to, linkMarkType);

      // If a new hyperlink URL is provided, apply it to the selected image
      if (href) {
        const mark = linkMarkType.create({
          href,
          target: '_blank',
          rel: 'noreferrer',
        });

        // Add the new link mark to the selected image
        transaction = transaction.addMark(from, to, mark);
      }
      // Only dispatch if the document actually changed
      if (transaction.docChanged) {
        dispatch(transaction);
      }
      // Reset local state so next open doesn't reuse stale URL
      this.imageHyperlinkUrl = '';
    }, 0);
  }

  // Used to insert tyhe custom upload image control to the editor
  private InjectInputFieldAsHtml(html: string, appendBefore: string) {
    const tryAdd = () => {
      const form = document.querySelector('.k-window-content form.k-form') ;
      if (!form) {
        return false;
      }
      const element = form.querySelector('label[for="' + appendBefore +'"]')?.closest('.k-form-field');

      if (element) {
        (element as HTMLElement).insertAdjacentHTML('afterend', html);
      } else {
        form.insertAdjacentHTML('beforeend', html);
      }
      return true;
    };

    // try immediately, else wait for dialog DOM
    if (tryAdd()) {
      return;
    }
    const obs = new MutationObserver(() => {
      if (tryAdd()) {
        obs.disconnect();
      }
    });
    obs.observe(document.body, { childList: true, subtree: true });
  }
  // Used to upload image from local system, change placeholder and adding value to the web address
  private OnFilePicked(e: Event) {
    try {
      const input = e.target as HTMLInputElement;
      if (!input || !input.files) {
        // Invalid file input
        return;
      }

      const file = input.files[0];
      if (!file) {
        // No file selected
        return;
      }

      // Validate file type
      if (!file.type.startsWith('image/')) {
        alert(this.$t('Image.Upload.ErrorChooseImageOnly').toString());
        return;
      }

      // Validate file size (max 5MB)
      const maxSize = 5 * 1024 * 1024; // 5MB
      if (file.size > maxSize) {
        alert(this.$t('Image.Upload.ErrorImageSize').toString());
        return;
      }
      const img = new Image();
      img.src = URL.createObjectURL(file);
      const objectUrl = URL.createObjectURL(file);
      img.onload = () => {
        URL.revokeObjectURL(objectUrl);
        if (img.naturalHeight > img.naturalWidth) {
          // Portrait: add height style
          const imageHeightInputElement = document.getElementById('k-editor-image-height') as HTMLInputElement;
          if(!!imageHeightInputElement) {
            imageHeightInputElement.value = img.naturalHeight > this.maxImageHeight ? this.maxImageHeight.toString() : img.naturalHeight.toString();
            const event = new Event('input', { bubbles: true });
            imageHeightInputElement.dispatchEvent(event);
          }
        } else {
          // Landscape or square: add width style
          const imageWidthInputElement = document.getElementById('k-editor-image-width') as HTMLInputElement;
          if(!!imageWidthInputElement) {
            imageWidthInputElement.value = img.naturalWidth > this.maxImageWidth ? this.maxImageWidth.toString() : img.naturalWidth.toString();
            const event = new Event('input', { bubbles: true });
            imageWidthInputElement.dispatchEvent(event);
          }
        }

        // Now you can continue with your upload or preview logic using this image element
      };
      this.fileToUpload = file;
      this.uploadedImage = URL.createObjectURL(this.fileToUpload);
      // Change the image source dynamically
      const placeholderImage = document.getElementById('imgTelerikImageUpload') as HTMLImageElement;
      if(!!placeholderImage) {
        placeholderImage.src = this.uploadedImage;
      }
      const webAddress = document.getElementById('k-editor-image-url') as HTMLInputElement;
      if (!!webAddress) {
        webAddress.value = this.uploadedImage;
        // Trigger 'input' event in case listeners are watching
        const event = new Event('input', { bubbles: true });
        webAddress.dispatchEvent(event);
      }
    } catch (error) {
      // Error handling file selection
      alert(this.$t('Message.SomeErrorOccured').toString());
    }
  }

  // Finds the native "Insert image" button in the editor toolbar
  private FindUploadImageButton(root: HTMLElement): HTMLButtonElement | null {
    try {
      // Search for buttons with "Insert image" or "Insert Image" titles
      const selectors = [
        'button[title="Insert image"]',
        'button[title="Insert Image"]',
        'button[aria-label*="Insert image" i]',
        'button[aria-label*="Insert Image" i]',
      ];

      for (const selector of selectors) {
        const button = root.querySelector(selector) as HTMLButtonElement;
        if (button) {
          return button;
        }
      }
      return null;
    } catch (error) {
      // Error finding native image button
      return null;
    }
  }
  // Finds the native "Insert HyperLink" button in the editor toolbar
  private FindLinkButton(root: HTMLElement): HTMLButtonElement | null {
    try {
      const span = root.querySelector('span.k-svg-i-link');
      if (span) {
        const button = span.parentElement as HTMLButtonElement | null;
        if (button && button.title === 'Insert hyperlink') {
          // You have the button element here
          return button;
        }
      }
      return null;
    } catch (error) {
      // Error finding native image button
      return null;
    }
  }
  // Build array of tags available
  private BuildAllowedTags() {
    const tagSet = new Set<string>();

    // Add tags from tools using flat()
    for (const tool of this.tools.flat()) {
      const tag = this.GetHtmlTagsForAvailableTools(tool);
      if (tag) {
        tagSet.add(tag);
      }
    }

    // Add <li> automatically
    if (tagSet.has('ul') || tagSet.has('ol')) {
      tagSet.add('li');
    }

    // Add p, br, strong and em tag to allowed tags
    tagSet.add('p');
    tagSet.add('em');
    tagSet.add('strong');
    tagSet.add('br');

    return Array.from(tagSet);
  }
  // Get corresponding html tag name from the tool
  private GetHtmlTagsForAvailableTools(tool: string): string | null {
    switch (tool) {
    case 'Bold':
      return 'b';
    case 'Italic':
      return 'i';
    case 'Underline':
      return 'u';
    case 'Link':
      return 'a';
    case 'OrderedList':
      return 'ol';
    case 'UnorderedList':
      return 'ul';
    default:
      return null;
    }
  }
  // Used to handle the paste operation in the editor
  private HandlePaste(e: any): string {
    const pastedHtml = e?.pastedHtml || '';

    if (!pastedHtml.trim()) {
      return '';
    }

    // Create temp DOM element to parse and clean HTML while preserving structure
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = pastedHtml;

    // 1. Remove all images completely
    const images = tempDiv.querySelectorAll('img');
    images.forEach((img) => img.remove());

    // 2. Remove style attributes entirely
    const styledElements = tempDiv.querySelectorAll('[style]');
    styledElements.forEach((el) => el.removeAttribute('style'));

    // 3. Sanitize html
    const cleanedHtml = this.SanitizeTags(tempDiv.innerHTML);

    // 4. Wrap phone numbers in tel: links
    return this.LinkPhoneNumbers(cleanedHtml);
  }
  // Sanitize copied html
  private SanitizeTags(html: string): string {
    const tagRegex = /<\/?([a-z0-9]+)[^>]*>/gi;
    const defaultTag = 'p';

    return html.replace(tagRegex, (fullMatch, tagName: string) => {
      const lower = tagName.toLowerCase();
      const isClosing = fullMatch.startsWith('</');

      // Keep allowed tags
      if (this.availableTags.includes(lower)) {
        return fullMatch;
      }

      // Convert tags to <p>
      if (this.blockLevelHTMLTags.includes(lower)) {
        return isClosing ? `</${defaultTag}>` : `<${defaultTag}>`;
      }

      // Any other tag drop it
      return '';
    });
  }

  // Wraps phone numbers in tel: links, skipping numbers already inside <a> tags
  private LinkPhoneNumbers(html: string): string {
    if (!html.trim()) {
      return html;
    }
    // Match existing anchor tags (to skip them) OR phone number patterns
    return html.replace(
      /(<a\b[^>]*>.*?<\/a>)|(\+?\d[\d\s\-\.\(\)]{6,18}\d)/gi,
      (match, anchorTag, phone) => {
        if (anchorTag) {
          return anchorTag;
        }
        const digits: string = phone.replace(/\D/g, '');
        const hasPlus: boolean = phone.trim().startsWith('+');
        const minDigits = hasPlus ? 7 : 10;
        if (digits.length < minDigits || digits.length > 15) {
          return match;
        }
        const telNumber: string = (hasPlus ? '+' : '') + digits;
        return '<a href="tel:' + telNumber + '">' + phone + '</a>';
      },
    );
  }

  // Before component unmounts remove all listners
  private beforeUnmount() {
    const root = this.GetEditorRootElement();
    if (root) {
      const nativeButton = this.FindUploadImageButton(root);
      if (!!nativeButton) {
        nativeButton.removeEventListener('click', this.HandleImageUpload);
      }
      const linkBtn = this.FindLinkButton(root);
      if(!!linkBtn) {
        linkBtn.removeEventListener('click', this.HandleLinkInsertion);
      }
    }

    // Remove OK button event listener
    const actionBtn = document.querySelector('.k-editor-window .k-actions .k-button-solid-primary') ;
    if (actionBtn) {
      actionBtn.removeEventListener('click', this.ApplyImageHyperlink);
    }

    // Remove listener attached to hyperlink popup url text field
    const webAddressURLInputElement = document.getElementById('k-editor-link-url');
    if(!!webAddressURLInputElement) {
      webAddressURLInputElement.removeEventListener('input', this.ValidateEditorLink);
    }
    // Remove listener attached to display text input in the hyper link popup
    const webAddressTextElement = document.getElementById('txtDisplayText');
    if(!!webAddressTextElement) {
      webAddressTextElement.removeEventListener('input', this.SetHyperLinkDisplayText);
    }
    // Remove File Input change listener
    if (this.onImageFileChange) {
      const imageUploadElement = document.getElementById('image-upload');
      if (imageUploadElement) {
        imageUploadElement.removeEventListener('change', this.onImageFileChange);
      }
      this.onImageFileChange = undefined;
    }
    // Remove image hyperlink URL listeners (safe-guard, usually dialog already destroyed)
    const imageLinkInput = document.getElementById('image-hyperlink-url');
    if (imageLinkInput) {
      imageLinkInput.removeEventListener('input', this.SetImageHyperlinkUrl);
    }
    // Remove custom event listeners
    Root.off('clearDescription');
    Root.off('UpdateEditorDescription');
  }

  // Watchers to sync prop changes into local state
  @Watch('description')
  private updateLocalText(val: string) {
    this.localText = val || '';
  }

  @Watch('isReadOnly')
  private updateReadOnly(val: boolean) {
    this.localIsReadOnly = val;
  }
  @Watch('imagesToBeUploaded')
  private ChckChangeInUploadedImagesData(val: TelerikImageUploadViewModel[]) {
    this.uploadedImagesData = val;
  }
}

export default toNative(Editor);
